/**
 * ChartTitleGenerator Service
 *
 * Generates intelligent chart titles based on data fields using LLM.
 * Features debouncing, caching, and fallback to humanized field names.
 */

import { api } from '@/services/api/client';

export interface TitleGenerationRequest {
  fieldNames: string[];
  chartType: 'line' | 'bar' | 'area';
  sampleData?: Record<string, unknown>[];
  /** Name of the data source/schema for better title context */
  dataSourceName?: string;
}

export interface TitleGenerationResponse {
  title: string;
  confidence: number;
  cached?: boolean;
  fallback?: boolean;
  latency?: number;
}

interface PendingRequest {
  resolve: (value: TitleGenerationResponse) => void;
  reject: (error: Error) => void;
}

export class ChartTitleGenerator {
  private static instance: ChartTitleGenerator;

  // Debounce timers by chart ID
  private debounceTimers: Map<string, ReturnType<typeof setTimeout>> = new Map();

  // Pending requests for deduplication
  private pendingRequests: Map<string, PendingRequest[]> = new Map();

  // Local cache for generated titles
  private titleCache: Map<string, { title: string; confidence: number; timestamp: number }> = new Map();

  // Cache TTL: 30 minutes
  private readonly cacheTtlMs = 1800000;

  // Debounce delay: 500ms
  private readonly debounceDelayMs = 500;

  // Max cache entries
  private readonly maxCacheSize = 100;

  private constructor() {
    // Private constructor for singleton
  }

  public static getInstance(): ChartTitleGenerator {
    if (!ChartTitleGenerator.instance) {
      ChartTitleGenerator.instance = new ChartTitleGenerator();
    }
    return ChartTitleGenerator.instance;
  }

  /**
   * Generate a chart title with debouncing
   *
   * @param chartId - Unique chart identifier for debouncing
   * @param fieldNames - Data field names from the chart
   * @param chartType - Type of chart (line, bar, area)
   * @param sampleData - Optional sample data for better context
   * @param dataSourceName - Optional data source/schema name for context
   * @returns Promise with generated title
   */
  public async generateTitle(
    chartId: string,
    fieldNames: string[],
    chartType: 'line' | 'bar' | 'area',
    sampleData?: Record<string, unknown>[],
    dataSourceName?: string
  ): Promise<TitleGenerationResponse> {
    // Cancel any pending debounce for this chart
    const existingTimer = this.debounceTimers.get(chartId);
    if (existingTimer) {
      clearTimeout(existingTimer);
    }

    return new Promise((resolve, reject) => {
      // Set up debounced execution
      const timer = setTimeout(async () => {
        this.debounceTimers.delete(chartId);

        try {
          const result = await this.executeGeneration(fieldNames, chartType, sampleData, dataSourceName);
          resolve(result);
        } catch (error) {
          reject(error);
        }
      }, this.debounceDelayMs);

      this.debounceTimers.set(chartId, timer);
    });
  }

  /**
   * Generate title immediately without debouncing
   */
  public async generateTitleImmediate(
    fieldNames: string[],
    chartType: 'line' | 'bar' | 'area',
    sampleData?: Record<string, unknown>[],
    dataSourceName?: string
  ): Promise<TitleGenerationResponse> {
    return this.executeGeneration(fieldNames, chartType, sampleData, dataSourceName);
  }

  /**
   * Execute the title generation (with caching and deduplication)
   */
  private async executeGeneration(
    fieldNames: string[],
    chartType: 'line' | 'bar' | 'area',
    sampleData?: Record<string, unknown>[],
    dataSourceName?: string
  ): Promise<TitleGenerationResponse> {
    // Generate cache key
    const cacheKey = this.getCacheKey(fieldNames, chartType);

    // Check local cache first
    const cached = this.titleCache.get(cacheKey);
    if (cached && Date.now() - cached.timestamp < this.cacheTtlMs) {
      return {
        title: cached.title,
        confidence: cached.confidence,
        cached: true
      };
    }

    // Check if there's already a pending request for the same parameters
    const pendingKey = cacheKey;
    if (this.pendingRequests.has(pendingKey)) {
      // Wait for the existing request
      return new Promise<TitleGenerationResponse>((resolve, reject) => {
        this.pendingRequests.get(pendingKey)!.push({ resolve, reject });
      });
    }

    // Initialize pending requests array
    this.pendingRequests.set(pendingKey, []);

    try {
      // Make API request
      const response = await api.post<TitleGenerationResponse>(
        '/ai/generate-chart-title',
        {
          fieldNames,
          chartType,
          sampleData: sampleData?.slice(0, 3), // Limit sample data
          dataSourceName // Include data source name for better context
        },
        {
          timeout: 5000 // 5 second timeout
        }
      );

      if (!response.success) {
        throw new Error('Title generation failed');
      }

      const result: TitleGenerationResponse = {
        title: response.data?.title || this.humanizeFieldNames(fieldNames, chartType, dataSourceName),
        confidence: response.data?.confidence || 0.5,
        cached: response.data?.cached,
        fallback: response.data?.fallback,
        latency: response.data?.latency
      };

      // Cache the result
      this.addToCache(cacheKey, result.title, result.confidence);

      // Resolve all pending requests
      const pending = this.pendingRequests.get(pendingKey) || [];
      pending.forEach(p => p.resolve(result));
      this.pendingRequests.delete(pendingKey);

      return result;

    } catch (error: unknown) {
      console.error('[ChartTitleGenerator] API call failed, using fallback', error);

      // Fallback to humanized field names (prefer dataSourceName)
      const fallbackTitle = this.humanizeFieldNames(fieldNames, chartType, dataSourceName);
      const result: TitleGenerationResponse = {
        title: fallbackTitle,
        confidence: 0.5,
        fallback: true
      };

      // Reject all pending requests with fallback result (resolve, not reject)
      const pending = this.pendingRequests.get(pendingKey) || [];
      pending.forEach(p => p.resolve(result));
      this.pendingRequests.delete(pendingKey);

      return result;
    }
  }

  /**
   * Generate cache key from field names and chart type
   */
  private getCacheKey(fieldNames: string[], chartType: string): string {
    const sortedFields = [...fieldNames].sort().join(',');
    return `${chartType}:${sortedFields}`;
  }

  /**
   * Add entry to local cache with LRU eviction
   */
  private addToCache(key: string, title: string, confidence: number): void {
    // Evict oldest entries if cache is full
    if (this.titleCache.size >= this.maxCacheSize) {
      const firstKey = this.titleCache.keys().next().value;
      if (firstKey !== undefined) {
        this.titleCache.delete(firstKey);
      }
    }

    this.titleCache.set(key, {
      title,
      confidence,
      timestamp: Date.now()
    });
  }

  /**
   * Humanize field names as fallback. Prefers dataSourceName when available.
   */
  public humanizeFieldNames(fieldNames: string[], chartType: string, dataSourceName?: string): string {
    // Prefer data source name (e.g., "water_depth" → "Water Depth")
    if (dataSourceName) {
      return this.humanizeFieldName(dataSourceName);
    }

    if (fieldNames.length === 0) {
      return `${this.capitalize(chartType)} Chart`;
    }

    // Filter out common non-descriptive field names
    const descriptiveFields = fieldNames.filter(f =>
      !['name', 'value', 'x', 'y', 'label', 'id', 'index', 'key',
        'timestamp', 'date', 'time', 'created_at', 'updated_at', 'created', 'updated'
      ].includes(f.toLowerCase())
    );

    if (descriptiveFields.length === 0) {
      return `${this.capitalize(chartType)} Chart`;
    }

    // Humanize the first descriptive field
    return this.humanizeFieldName(descriptiveFields[0]);
  }

  /**
   * Humanize a single field name
   */
  private humanizeFieldName(fieldName: string): string {
    return fieldName
      .replace(/([A-Z])/g, ' $1') // Add space before capitals
      .replace(/[_-]/g, ' ')      // Replace underscores and dashes with spaces
      .replace(/\s+/g, ' ')       // Normalize whitespace
      .trim()
      .split(' ')
      .map(word => this.capitalize(word))
      .join(' ');
  }

  /**
   * Capitalize first letter
   */
  private capitalize(str: string): string {
    return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
  }

  /**
   * Clear all caches
   */
  public clearCache(): void {
    this.titleCache.clear();
  }

  /**
   * Cancel pending debounced request for a chart
   */
  public cancelPending(chartId: string): void {
    const timer = this.debounceTimers.get(chartId);
    if (timer) {
      clearTimeout(timer);
      this.debounceTimers.delete(chartId);
    }
  }

  /**
   * Get cache statistics
   */
  public getCacheStats(): { size: number; maxSize: number } {
    return {
      size: this.titleCache.size,
      maxSize: this.maxCacheSize
    };
  }
}

// Export singleton instance
export const chartTitleGenerator = ChartTitleGenerator.getInstance();
